import { FC } from 'react';
import { Region } from '@hooks/useRegions/types';
import {
  TimeTradingEnum,
  TimeTradingLabels,
} from '@pages/CalculatorPage/ApplicantDetailsPage/SalariedDirectorFields/types';
import { defaultRegionValue } from '@pages/CalculatorPage/helpers';
import { AffordabilityCalculatorForm, CurrencyPeriodEnum } from '@pages/CalculatorPage/types';
import { useFormContext } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { ApplicantDetailsReviewQuestions, FinanceDetailsReviewQuestions } from '../questions';
import { customFormattingFieldsList, fieldsWithUnusulMultiplier, isFalseValue } from './helpers';
import { ReviewSectionProps } from './types';

export const ReviewBlock: FC<ReviewSectionProps> = ({
  titleKey,
  reviewRowKeys,
  onEditClick,
  hideEditButton,
  subTitleKey,
  subTitleValues,
  titleValues,
}) => {
  const intl = useIntl();
  const form = useFormContext<AffordabilityCalculatorForm>();

  const currencyFormatter = (number: number) =>
    intl
      .formatNumber(number, {
        style: 'currency',
        currency: 'GBP',
      })
      .replace('£', '£ ');

  const termFormatter = (term: { years: number; months: number }) =>
    intl.formatMessage({ id: FinanceDetailsReviewQuestions.term.value }, { years: term.years, months: term.months });

  const employmentTypeFormatter = (employmentType: string) =>
    intl.formatMessage({ id: ApplicantDetailsReviewQuestions.income.employmentTypeValue + employmentType });

  const timeTradingFormatter = (timeTrading: TimeTradingEnum) =>
    intl.formatMessage({ id: TimeTradingLabels[timeTrading] });

  const regionFormatter = (region: keyof typeof Region | null) => (region ? Region[region] : defaultRegionValue);

  const commonFieldFormatter = (fieldValue: any, fieldValueKey: string) => {
    if (isFalseValue(fieldValue)) return '-';

    let multiplier = 1;
    const isFieldFromBenefitsSection = fieldsWithUnusulMultiplier.some(field => fieldValueKey.includes(field));
    const fieldPeriodValue = form.getValues([
      (fieldValueKey + 'Period') as keyof AffordabilityCalculatorForm,
    ])[0] as any as CurrencyPeriodEnum | undefined;

    if (isFieldFromBenefitsSection) multiplier = 13;
    else if (fieldPeriodValue === CurrencyPeriodEnum.perMonth) multiplier = 12;

    switch (typeof fieldValue) {
      case 'number':
        return currencyFormatter(fieldValue * multiplier);
      case 'string':
        if (fieldValue === '') return currencyFormatter(0);
        return fieldValue;
      default:
        return fieldValue;
    }
  };

  const customFieldFormatter = (fieldValue: any, fieldLabel: string) => {
    if (isFalseValue(fieldValue)) return '-';

    switch (fieldLabel) {
      case FinanceDetailsReviewQuestions.term.labelKey:
        return termFormatter(fieldValue);
      case ApplicantDetailsReviewQuestions.income.employmentType:
        return employmentTypeFormatter(fieldValue);
      case ApplicantDetailsReviewQuestions.income.timeTrading:
        return timeTradingFormatter(fieldValue);
      case FinanceDetailsReviewQuestions.regionKey:
        return regionFormatter(fieldValue);
      case FinanceDetailsReviewQuestions.dependentsKey:
      case FinanceDetailsReviewQuestions.productKey:
      case FinanceDetailsReviewQuestions.numberOfApplicantsKey:
      case ApplicantDetailsReviewQuestions.income.timeRemainingMonths:
      case ApplicantDetailsReviewQuestions.income.timeRemainingYears:
        return fieldValue;
    }
  };

  return (
    <div>
      {titleKey && (
        <div className={'xs:mb-6 xs:mt-9 flex items-center justify-between md:mb-12 md:mt-16'}>
          <h2 className={'xs:text-xl text-dark font-satoshi-black m-0 font-black md:text-2xl'}>
            <FormattedMessage id={titleKey} values={titleValues} />
          </h2>

          {!hideEditButton && (
            <p
              onClick={onEditClick}
              className={'font-satshiMedium text-dark m-0 cursor-pointer text-xl font-medium underline'}
            >
              {intl.formatMessage({ id: 'cta.edit' }).toLocaleLowerCase()}
            </p>
          )}
        </div>
      )}
      {subTitleKey && (
        <h3 className={'text-dark font-satoshi-black xs:mt-6 xs:mb-4 m-0 text-xl font-black md:mb-7 md:mt-12'}>
          <FormattedMessage id={subTitleKey} values={subTitleValues} />
        </h3>
      )}
      {reviewRowKeys.map((item, index) => {
        const fieldLabelKey = item.labelKey;
        const fieldValueKey = item.valueKey as keyof AffordabilityCalculatorForm;
        const fieldValue = form.getValues([fieldValueKey])[0];
        const formattedFieldValue = customFormattingFieldsList.includes(fieldLabelKey)
          ? customFieldFormatter(fieldValue, fieldLabelKey)
          : commonFieldFormatter(fieldValue, fieldValueKey);

        return (
          <div
            key={index}
            className={
              'text-ash border-dust xs:text-base m-0 mb-4 flex justify-between border-x-0 border-b border-t-0 border-solid pb-3 md:text-lg'
            }
          >
            <p className={'font-satoshi-black m-0 mr-2 w-1/2 font-black'}>
              <FormattedMessage id={fieldLabelKey} />
            </p>
            <p className={'font-satoshi-medium m-0 w-1/2 font-medium'}>{formattedFieldValue}</p>
          </div>
        );
      })}
    </div>
  );
};
